import { Timestamp } from 'firebase/firestore';

/**
 * Unified TestMetadata interface used across the application
 * This combines all fields from different implementations
 */
export interface TestMetadata {
  // Core identification fields
  testId: string;
  userId: number | string; // Can be number (regular app) or UUID string (WebView)
  tenantId: number | string; // Can be number or string depending on context

  // Study task related
  studyTaskId?: string;
  testTitle?: string;
  testMode?: string; // 'optical' | 'drawing' | etc.

  // User information
  userName?: string;

  // Question tracking
  lastActiveQuestionId?: string;
  lastActiveQuestionIndex?: number;
  completedQuestions?: string[];
  defaultAnswerType?: number; // Test default option count (1: 4 options, 2: 5 options)

  // Progress tracking
  totalQuestions?: number;
  answeredQuestions?: number;

  // Timing fields
  startTime: number | Timestamp; // Can be number or Firebase Timestamp
  lastUpdateTime?: number;
  lastActiveAt?: number; // Deprecated - use lastUpdateTime
  totalTime?: number; // Total allowed time in seconds
  endTime?: number | Timestamp;

  // Status tracking
  status?: 'in_progress' | 'completed' | 'synced' | 'sync_failed';
  isCompleted?: boolean; // Deprecated - use status instead

  // Bulk submission configuration
  bulkSubmitUrl?: string | null;
  bulkSubmitEnabled?: boolean;

  // MSSQL sync tracking
  syncedToMssql?: boolean;
  syncAttempts?: number;
  lastSyncAttempt?: Timestamp | any;
  syncError?: string | null;

  // Timestamps
  createdAt?: Timestamp | any;
  updatedAt?: Timestamp | any;
}

/**
 * Helper type for creating test metadata
 * Makes all fields optional except required ones
 */
export type CreateTestMetadataInput = Pick<TestMetadata, 'testId' | 'userId' | 'tenantId'> &
  Partial<Omit<TestMetadata, 'testId' | 'userId' | 'tenantId'>>;

/**
 * Helper type for updating test metadata
 * All fields are optional
 */
export type UpdateTestMetadataInput = Partial<TestMetadata>;

// Firebase collection name constant
export const TEST_METADATA_COLLECTION = 'testMetadata';

// Helper function to create document key
export function createTestMetadataKey(tenantId: number | string, testId: string): string {
  // Ensure tenantId is never undefined
  const safeTenantId = tenantId || '50000';
  return `${safeTenantId}_${testId}`;
}